import { useEffect, useRef } from "react";

/**
 * Animované hvězdné pozadí. Canvas-based — lehké, GPU-friendly.
 * Generuje ~200 hvězd s varying jasem, několik jemně padá, některé blikají.
 */
export default function StarField({
  density = 0.00012,
  shootingStars = true,
}: {
  density?: number;
  shootingStars?: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let stars: Star[] = [];
    let shooters: Shooter[] = [];
    let rafId = 0;

    function resize() {
      const dpr = window.devicePixelRatio || 1;
      width = canvas!.clientWidth;
      height = canvas!.clientHeight;
      canvas!.width = width * dpr;
      canvas!.height = height * dpr;
      ctx!.scale(dpr, dpr);

      // Regenerate stars
      const count = Math.max(80, Math.floor(width * height * density));
      stars = Array.from({ length: count }, () => makeStar(width, height));
    }

    function makeStar(w: number, h: number): Star {
      const size = Math.random() * 1.8 + 0.3;
      return {
        x: Math.random() * w,
        y: Math.random() * h,
        r: size,
        baseAlpha: 0.3 + Math.random() * 0.7,
        twinkleSpeed: 0.002 + Math.random() * 0.008,
        twinklePhase: Math.random() * Math.PI * 2,
        color: Math.random() > 0.92 ? "#fde68a" : Math.random() > 0.85 ? "#93c5fd" : "#ffffff",
      };
    }

    function spawnShooter() {
      if (!shootingStars) return;
      const fromLeft = Math.random() > 0.5;
      shooters.push({
        x: fromLeft ? -50 : width + 50,
        y: Math.random() * height * 0.5,
        vx: fromLeft ? 8 + Math.random() * 6 : -(8 + Math.random() * 6),
        vy: 2 + Math.random() * 2,
        life: 0,
        maxLife: 80 + Math.random() * 40,
      });
    }

    let frame = 0;
    function tick() {
      ctx!.clearRect(0, 0, width, height);

      // Hvězdy
      for (const s of stars) {
        s.twinklePhase += s.twinkleSpeed;
        const alpha = s.baseAlpha * (0.5 + 0.5 * Math.sin(s.twinklePhase));
        ctx!.fillStyle = s.color;
        ctx!.globalAlpha = alpha;
        ctx!.beginPath();
        ctx!.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx!.fill();
      }
      ctx!.globalAlpha = 1;

      // Padající hvězdy — občas
      frame++;
      if (shootingStars && frame % 240 === 0 && Math.random() > 0.3) spawnShooter();

      shooters = shooters.filter((sh) => {
        sh.x += sh.vx;
        sh.y += sh.vy;
        sh.life += 1;

        const tailLen = 80;
        const alpha = Math.max(0, 1 - sh.life / sh.maxLife);

        const grad = ctx!.createLinearGradient(
          sh.x,
          sh.y,
          sh.x - sh.vx * (tailLen / 4),
          sh.y - sh.vy * (tailLen / 4)
        );
        grad.addColorStop(0, `rgba(255, 255, 255, ${alpha})`);
        grad.addColorStop(1, "rgba(255, 255, 255, 0)");
        ctx!.strokeStyle = grad;
        ctx!.lineWidth = 1.5;
        ctx!.beginPath();
        ctx!.moveTo(sh.x, sh.y);
        ctx!.lineTo(sh.x - sh.vx * (tailLen / 4), sh.y - sh.vy * (tailLen / 4));
        ctx!.stroke();

        return sh.life < sh.maxLife && sh.x > -100 && sh.x < width + 100;
      });

      rafId = requestAnimationFrame(tick);
    }

    resize();
    tick();

    const onResize = () => resize();
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
    };
  }, [density, shootingStars]);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute inset-0 h-full w-full"
      aria-hidden="true"
    />
  );
}

type Star = {
  x: number;
  y: number;
  r: number;
  baseAlpha: number;
  twinkleSpeed: number;
  twinklePhase: number;
  color: string;
};

type Shooter = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
};
